import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitPullRequest, Layers } from 'lucide-react';
import { Button, Callout, Card, PageHeader, Table, type Column } from '@/shared/ui';
import { useToast } from '@/shared/ui/Toast';
import { AgentChip } from '@/modules/agents/components/AgentChip';
import { skillLabel } from '@/modules/agents/components/labels';
import type { AgentIdentity } from '@/modules/agents/utils/agentLabels';
import { overridesApi } from '../api/overridesApi';
import { registryApi } from '../api/registryApi';
import { overrideKeys, registryKeys } from '../queryKeys';
import { expiryLabel } from '../utils/textDiff';
import {
  DiffModal,
  NewOverrideModal,
  PromoteModal,
  type OverrideRow as Row,
} from '../components/registry/OverrideModals';
import { overrideErrorMessage as errorMessage } from '../components/registry/overrideErrorMessage';
import styles from './AgencySkillOverridesPage.module.css';

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AgencySkillOverridesPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [diffRow, setDiffRow] = useState<Row | null>(null);
  const [promoteRow, setPromoteRow] = useState<Row | null>(null);
  const now = Date.now();

  const overrides = useQuery({
    queryKey: overrideKeys.list(),
    queryFn: () => overridesApi.listAll(),
  });
  const registry = useQuery({
    queryKey: registryKeys.registry(),
    queryFn: () => registryApi.getRegistry(),
    staleTime: 5 * 60_000,
  });
  const writeBack = useQuery({
    queryKey: registryKeys.writeBack(),
    queryFn: () => registryApi.getWriteBack(),
    staleTime: 5 * 60_000,
  });
  const writeBackConfigured = writeBack.data?.configured ?? false;
  const agents = useMemo<AgentIdentity[]>(
    () =>
      (registry.data?.roles ?? []).map((r) => ({
        roleKey: r.key,
        personaName: r.persona?.name ?? r.displayName,
        displayName: r.displayName,
      })),
    [registry.data],
  );
  const agentFor = useMemo(
    () => new Map((registry.data?.skills ?? []).map((s) => [s.key, s.agentType])),
    [registry.data],
  );

  const retire = useMutation({
    mutationFn: (row: Row) =>
      overridesApi.retire(
        row.practice.id,
        row.location.id,
        row.id,
        'retired from the Command Center',
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overrideKeys.all });
      toast.success('Override retired. The global skill applies from the next run.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const rows = useMemo<Row[]>(
    () => (overrides.data ?? []).map((o) => ({ ...o })),
    [overrides.data],
  );

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'where',
        header: 'Practice · location',
        render: (row) => (
          <span className={styles.cellStack}>
            <span className={styles.cellName}>{row.practice.name}</span>
            <span className={styles.cellSub}>{row.location.name}</span>
          </span>
        ),
      },
      {
        key: 'skill',
        header: 'Skill',
        render: (row) => (
          <span className={styles.cellStack}>
            <span className={styles.cellName}>{skillLabel(row.skillKey)}</span>
            <span className={styles.cellSub}>
              <AgentChip agentType={agentFor.get(row.skillKey)} agents={agents} compact />{' '}
              <span className={styles.mono}>{row.skillKey}</span>
            </span>
          </span>
        ),
      },
      {
        key: 'what',
        header: 'Changes',
        render: (row) => (
          <span className={styles.cellSub}>
            {[
              row.method !== undefined && 'method',
              row.references !== undefined && 'references',
              row.objective !== undefined && 'objective',
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        ),
      },
      {
        key: 'reason',
        header: 'Reason',
        render: (row) => <span className={styles.reason}>{row.reason}</span>,
      },
      {
        key: 'expiry',
        header: 'Expiry',
        render: (row) => {
          const e = expiryLabel(row.expiresAt, now);
          const cls =
            e.tone === 'ok'
              ? styles.expiryOk
              : e.tone === 'soon'
                ? styles.expirySoon
                : styles.expiryReview;
          return <span className={cls}>{e.text}</span>;
        },
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        render: (row) => (
          <span className={styles.actions}>
            <Button size="sm" variant="secondary" onClick={() => setDiffRow(row)}>
              Diff
            </Button>
            <span
              title={
                writeBackConfigured
                  ? undefined
                  : 'Write-back is not configured on the backend — this screen is read-only until it is (GITHUB_REPO and friends).'
              }
            >
              <Button
                size="sm"
                variant="secondary"
                disabled={!writeBackConfigured}
                onClick={() => setPromoteRow(row)}
              >
                Promote
              </Button>
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={retire.isPending}
              onClick={() => retire.mutate(row)}
            >
              Retire
            </Button>
          </span>
        ),
      },
    ],
    [agents, agentFor, now, retire, writeBackConfigured],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title="Practice Overrides"
        subtitle="Temporary, practice-specific versions of a skill's methodology. Every override is time-boxed and flagged so it never silently drifts from the global source of truth."
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            + New override
          </Button>
        }
      />
      <Callout icon={Layers} variant="accent" title="Overrides are the exception">
        Global skills apply to every practice. An override changes only what the agent reads for one
        location — the method body, its references, the objective — and never its tools, mode or
        schedule. Promote what proves out into the global skill through a pull request, then retire
        the override.
      </Callout>
      <Card>
        <Table
          columns={columns}
          data={rows}
          isLoading={overrides.isLoading}
          emptyMessage={
            overrides.isError
              ? 'Overrides could not load.'
              : 'No active overrides. Every practice runs the global skills.'
          }
        />
      </Card>
      <NewOverrideModal open={creating} onClose={() => setCreating(false)} agents={agents} />
      <DiffModal row={diffRow} onClose={() => setDiffRow(null)} />
      <PromoteModal
        row={promoteRow}
        onClose={() => setPromoteRow(null)}
        onDone={(change) => {
          setPromoteRow(null);
          queryClient.invalidateQueries({ queryKey: registryKeys.all });
          toast.success(
            `Pull request #${change.prNumber} opened. Retire the override once it merges.`,
          );
        }}
      />
      {rows.some((r) => r.expiresAt === undefined) && (
        <Callout icon={GitPullRequest} variant="warning" title="Some overrides have no expiry">
          An override without an expiry is easy to forget. Promote it or set a date by retiring and
          re-creating it.
        </Callout>
      )}
    </div>
  );
}
